/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { Headline, Layout, LayoutItem, Tag } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import styled from 'styled-components';
import { transformServices } from '../../lib/transformServices';
import { messages } from '../../i18n/messages';
import { useTrackingManager } from '../useTrackingManager';
import { useMedia } from '../../lib/hooks';
import { Image } from './Image';
import {
  SYS_BORDER_RADIUS_MD,
  SYS_SPACE_FIXED_100,
  SYS_SPACE_FIXED_150,
  SYS_SPACE_FIXED_300,
} from '@oneaudi/unified-web-common';

const StyledImage = styled.figure`
  position: relative;
  margin: 0;
  /* create aspect ratio box with an aspect ratio of 16:9 */
  padding: 0 0 56.25% 0;
  overflow: hidden;

  > img {
    display: block;
    position: absolute;
    width: 100%;
    height: auto;
    ${!process.env.IS_LEGACY && `border-radius: ${SYS_BORDER_RADIUS_MD}`};
  }
`;

const StyledHeadlineAsLink = styled.div`
  > a {
    color: ${process.env.IS_LEGACY ? '#333' : '#fcfcfd'};
    &:hover {
      color: ${process.env.IS_LEGACY ? '#4d4d4d' : 'hsla(216, 33%, 99%, 0.7)'};
    }

    text-decoration: none;
  }
`;

type HeadlineOrder = '1' | '2' | '3' | '4';
type HeadlineType =
  | 'caption'
  | 'div'
  | 'figcaption'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'label'
  | 'span';
interface PartnerBusinessCardHeaderProps {
  partner: any;

  isNarrow?: boolean;
  displayOfficialName?: boolean;

  showImage?: boolean;

  showServiceList?: boolean;

  isLinkedToPWS?: boolean;

  headline: {
    headlineTag: HeadlineType;
    headlineOrder: HeadlineOrder;
  };
}

export const PartnerBusinessCardHeader = (
  props: PartnerBusinessCardHeaderProps,
): React.ReactElement => {
  const {
    partner,
    isNarrow,
    displayOfficialName = true,
    showImage = true,
    showServiceList = true,
    isLinkedToPWS = true,
    headline,
  } = props;
  const trackingManager = useTrackingManager();
  const { headlineOrder, headlineTag } = headline;

  const partnerServiceLabels = new Map([
    ['sales', useI18n(messages.partnerServiceSales)],
    ['sales-r8', useI18n(messages.partnerServiceSalesR8)],
    ['service', useI18n(messages.partnerServiceService)],
    ['etron', useI18n(messages.partnerServiceEtron)],
    ['usedcars', useI18n(messages.partnerServiceUsedcars)],
    ['audi-on-demand', useI18n(messages.partnerServiceAudiOnDemand)],
  ]);
  const services: any = transformServices(
    partner.services,
    partner.additionalServices,
    partnerServiceLabels,
  );
  // // TODO Check imgWidth value
  const imgWidth = 328;

  const isViewportM = useMedia(`only screen and (min-width: 1023px) `);
  const dealerNameLinkAriaLabel = useI18n(messages.dealerNameLinkAriaLabel);

  const showColumnItemPicture = (showImage && isNarrow) || (showImage && !isViewportM && !isNarrow);
  const partnerName = displayOfficialName ? partner.officialName : partner.nonOfficialName;
  const [partnerImgSrc] = partner.locationImageUrl.split('?');
  const margin = !showServiceList
    ? ['var(--spacing-relative-lg)', 0, 'var(--spacing-relative-lg)', 0]
    : [0, 0, 0, 0];

  const partnerImage = (
    <StyledImage>
      <Image
        loadingAnimation="fade"
        src={`${partnerImgSrc}?preferwebp=true?width=${imgWidth}`}
        data-testid="image"
        alt={useI18n(messages.dealerImageAltText)}
        sizes={[
          {
            condition: `(max-width: ${imgWidth}px)`,
            value: `${imgWidth}px`,
          },
          {
            condition: '(max-width: 639px)',
            value: '656px',
          },
          {
            condition: '(max-width: 1023px)',
            value: `${imgWidth}px`,
          },
          {
            value: '656px',
          },
        ]}
        srcSet={[
          {
            src: `${partnerImgSrc}?preferwebp=true?width=${imgWidth}`,
            desc: `${imgWidth}w`,
          },
          {
            src: `${partnerImgSrc}?preferwebp=true?width=656`,
            desc: '656w',
          },
        ]}
      />
    </StyledImage>
  );

  return (
    <Layout
      direction={{ xs: 'column', lg: 'row' }}
      wrap="nowrap"
      align="stretch"
      justify="space-between"
    >
      <LayoutItem align="stretch">
        <Headline
          tag={headlineTag}
          variant={{ type: 'headline', order: headlineOrder, weight: 'normal' }}
          data-testid="name"
          margin={margin as any}
        >
          {isLinkedToPWS ? (
            <StyledHeadlineAsLink>
              <a
                onClick={() => {
                  trackingManager.click(
                    'exit_link',
                    partner.url,
                    'headline',
                    partnerName,
                    partnerName,
                    '1',
                    'pws link',
                  );
                }}
                aria-label={dealerNameLinkAriaLabel}
                href={partner.url}
                target="_blank"
                rel="noreferrer"
              >
                {partnerName}
              </a>
            </StyledHeadlineAsLink>
          ) : (
            partnerName
          )}
        </Headline>
        {showServiceList ? (
          <LabelsContainer data-testid="services">
            {services?.map((service: string) => (
              <Tag size="sm" label={service} variant="read-only" key={service} />
            ))}
          </LabelsContainer>
        ) : null}
        {partner.locationImageUrl && showColumnItemPicture && (
          <LayoutItem margin={[0, 0, 'var(--spacing-relative-lg)', 0]}>
            {isLinkedToPWS ? (
              <a
                onClick={() => {
                  trackingManager.click(
                    'exit_link',
                    partner.url,
                    'image',
                    '',
                    partnerName,
                    '1',
                    'pws link',
                  );
                }}
                href={partner.url}
                target={isNarrow ? '_blank' : ''}
                rel="noreferrer"
              >
                {partnerImage}
              </a>
            ) : (
              partnerImage
            )}
          </LayoutItem>
        )}
      </LayoutItem>
    </Layout>
  );
};

const LabelsContainer = styled.div`
  margin: ${SYS_SPACE_FIXED_150} 0 ${SYS_SPACE_FIXED_300};
  display: inline-flex;
  gap: ${SYS_SPACE_FIXED_100} ${SYS_SPACE_FIXED_100};
  flex-wrap: wrap;
`;
